export const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
};

let getAccessToken: (() => string | null) | null = null;

export function setTokenGetter(getter: () => string | null) {
  getAccessToken = getter;
}

/** Get the current access token (for manual fetch calls like file uploads) */
export function getCurrentToken(): string | null {
  return getAccessToken?.() || null;
}

export async function apiClient<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { body, headers: customHeaders, ...rest } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(customHeaders as Record<string, string>),
  };

  const token = getAccessToken?.();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...rest,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      statusCode: response.status,
      message: response.statusText,
    }));
    throw error;
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

/**
 * Upload a file as multipart/form-data with a single file field.
 * Used for file-only upload flows (e.g., order import pre-processing).
 */
export async function uploadFile<T = unknown>(
  path: string,
  file: File,
  fieldName: string = 'file',
): Promise<T> {
  const formData = new FormData();
  formData.append(fieldName, file);

  const headers: Record<string, string> = {};
  const token = getAccessToken?.();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      statusCode: response.status,
      message: response.statusText,
    }));
    throw error;
  }

  return response.json() as Promise<T>;
}

/**
 * Submit a multipart/form-data request with optional file attachment.
 * Used for booklet submission create/edit flows that include file uploads.
 */
export async function uploadSubmission<T = unknown>(
  path: string,
  method: 'POST' | 'PATCH',
  fields: Record<string, string>,
  file?: File,
): Promise<T> {
  const formData = new FormData();

  Object.entries(fields).forEach(([key, value]) => {
    if (value != null && value !== '') {
      formData.append(key, value);
    }
  });

  if (file) {
    formData.append('file', file);
  }

  const headers: Record<string, string> = {};
  const token = getAccessToken?.();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      statusCode: response.status,
      message: response.statusText,
    }));
    throw error;
  }

  return response.json() as Promise<T>;
}
